import { FC, FormEvent } from "react";
import { useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";

import Button from "@/components/Button";
import { Modal } from "@/components/Modal";
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from "@/components/SidePanel";
import useAlerts from "@/hooks/useAlerts";
import { handleFormErrors } from "@/utils/helpers";
import { api } from "@/utils/api";
import { FormLayout } from "@/components/Forms";
import useOnCloseWithConfirmation from "@/hooks/useOnCloseWithConfirmation";
import FormField from "@/components/Forms/FormField";
import { MetaBusinessAccountResource } from "@/types/resources";

interface FormDataT {
  name: string;
  app_id: string;
  access_token?: string;
  notes: string;
}

interface Props {
  onClose: () => void;
  editableAccount?: MetaBusinessAccountResource;
}

const Form: FC<Props> = ({ onClose, editableAccount }) => {
  const queryClient = useQueryClient();

  const {
    handleSubmit,
    clearErrors,
    setError,
    control,
    formState: { isSubmitting, isDirty },
  } = useForm<FormDataT>({
    defaultValues: {
      name: editableAccount?.name ?? "",
      app_id: editableAccount?.appId ?? "",
      notes: editableAccount?.notes ?? "",
      ...(!!editableAccount && {
        access_token: "",
      }),
    },
  });

  const alerts = useAlerts();

  const closeDialog = useOnCloseWithConfirmation(onClose, isDirty);

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    handleSubmit(async (data) => {
      try {
        if (editableAccount) {
          await api.put(
            `/v1/meta-business-accounts/${editableAccount.id}`,
            data
          );
        } else {
          await api.post("/v1/meta-business-accounts", data);
        }

        await queryClient.invalidateQueries(["meta_business_accounts"]);

        alerts.success(
          `Account was successfully ${
            !!editableAccount ? "updated" : "created"
          }`
        );

        onClose();
      } catch (err) {
        handleFormErrors(err, setError, alerts);
      }
    })(e);
  };

  return (
    <Modal>
      <SidePanel
        heading={`${editableAccount ? "Edit" : "New"} business account`}
        onClose={closeDialog}
      >
        <SidePanelBody autoComplete="off" onSubmit={onSubmit}>
          <SidePanelContent>
            <FormLayout>
              <FormField<FormDataT>
                control={control}
                name="name"
                label="Account Name"
              />
              {!editableAccount && (
                <FormField<FormDataT>
                  control={control}
                  name="access_token"
                  label="System User Token"
                  placeholder="Add token"
                />
              )}
              <FormField<FormDataT>
                control={control}
                name="app_id"
                label="Facebook App ID"
                placeholder="Add ID"
              />
              <FormField<FormDataT>
                control={control}
                name="notes"
                type="textarea"
                label="Note"
                placeholder="Add note"
              />
            </FormLayout>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button
              disabled={isSubmitting}
              loading={isSubmitting}
              type="submit"
            >
              {editableAccount ? "Update" : "Create"}
            </Button>
            <Button color="secondary" onClick={closeDialog} type="button">
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default Form;
